<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'FieldsetContractServiceFee',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    item: {
      type: Object,
      required: true,
    },
    labelAlign: {
      type: String,
      required: false,
      default: 'right',
    },
    readonly: {
      type: Boolean,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const currencies = ref(null);
  const ux = ref([]);

  const $data = computed(() => ({
    currencies: currencies.value,
    ux: ux.value,
  }));

  const $$load_currencies = async () => {
    return $fn.value.fetch('list_currency');
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [{ name: 'currencies' }, { name: 'ux' }],
    loaders: { currencies: $$load_currencies },
    ux: {},
    dataRefs: { currencies, ux },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  watch(
    () => props.item,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('item');
    }
  );
  watch(
    () => props.labelAlign,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('labelAlign');
    }
  );
  watch(
    () => props.readonly,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('readonly');
    }
  );

  const act_7315092382398562_reloadComponentData_7e7eovfhb =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_vsvygb1vo =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_7e7eovfhb,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_vsvygb1vo],
    event: 'reload',
  });

  const actions = {
    inst_evh_7315092382398562_reload_vsvygb1vo,
    inst_evh_7315092382398562_reload,
  };

  const evh_7315092382398562_reload_vsvygb1vo = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_vsvygb1vo.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpUsercontrol
    :loading="loading"
    :reloading="reloading"
    :loaded="loaded"
    :error="error"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <ItpFormGroup
      name="fee_valid_from"
      :label="pgettext('ContractServiceFee', 'Validity')"
      :label-cols-md="3"
      :content-cols-md="7"
      :labelAlign="labelAlign"
      :helpTooltip="
        pgettext(
          'ContractServiceFee|Tooltip',
          'The validity period of the fee.'
        )
      "
      contentClass="row"
    >
      <div class="col">
        <ItpFormDatetime name="valid_from" v-model="item.valid_from" required>
        </ItpFormDatetime>
      </div>
      <div class="col">
        <ItpFormDatetime name="valid_to" v-model="item.valid_to">
        </ItpFormDatetime>
      </div>
      <template #description>
        <div>
          <span>
            {{
              pgettext(
                'ContractServiceFee|Tooltip',
                'The start date is required. Set the end date only if different from the end of the service.'
              )
            }}
          </span>
        </div>
      </template>
    </ItpFormGroup>
    <ItpFormGroup
      name="fee_valid_to"
      :label="pgettext('ContractServiceFee', 'Amount')"
      :label-cols-md="3"
      :content-cols-md="5"
      :labelAlign="labelAlign"
      contentClass="input-group"
      :helpTooltip="
        pgettext('ContractServiceFee|Tooltip', 'The price per unit of the fee.')
      "
    >
      <ItpFormInput
        mask="number"
        unmask="typed"
        name="fee_amount"
        :scale="4"
        v-model="item.amount"
        :value="item.amount"
        @input="
          !($event && typeof $event.stopPropagation === 'function') &&
          (item.amount =
            $event && typeof $event === 'object' && 'data' in $event
              ? $event.data
              : $event)
        "
        required
        class="col-9"
      >
      </ItpFormInput>
      <ItpFormSelect2
        name="currency"
        v-model="item.currency"
        :value="item.currency"
        @input="
          !($event && typeof $event.stopPropagation === 'function') &&
          (item.currency =
            $event && typeof $event === 'object' && 'data' in $event
              ? $event.data
              : $event)
        "
        :options="currencies"
        textField="code"
        valueField="code"
        required
        class="col-3"
      >
      </ItpFormSelect2>
    </ItpFormGroup>
    <ItpFormGroup
      name="fee_unit"
      :label="pgettext('ContractServiceFee', 'Invoice unit')"
      :label-cols-md="3"
      :content-cols-md="2"
      :labelAlign="labelAlign"
      contentClass="input-group"
      :helpTooltip="
        pgettext('ContractServiceFee|Tooltip', 'The unit used for billing.')
      "
    >
      <ItpFormInput
        name="fee_unit"
        v-model="item.unit"
        :value="item.unit"
        @input="
          !($event && typeof $event.stopPropagation === 'function') &&
          (item.unit =
            $event && typeof $event === 'object' && 'data' in $event
              ? $event.data
              : $event)
        "
        required
      >
      </ItpFormInput>
    </ItpFormGroup>
    <ItpFormGroup
      name="fee_quantity"
      :label="pgettext('ContractServiceFee', 'Quantity')"
      :label-cols-md="3"
      :content-cols-md="2"
      :labelAlign="labelAlign"
      contentClass="input-group"
      :helpTooltip="
        pgettext(
          'ContractServiceFee|Tooltip',
          'The quantity to bill for each period.'
        )
      "
    >
      <ItpFormInput
        type="number"
        name="fee_quantity"
        v-model="item.quantity"
        :value="item.quantity"
        @input="
          !($event && typeof $event.stopPropagation === 'function') &&
          (item.quantity =
            $event && typeof $event === 'object' && 'data' in $event
              ? $event.data
              : $event)
        "
      >
      </ItpFormInput>
    </ItpFormGroup>
    <ItpFormGroup
      name="fee_unit_of_quantity"
      :label="pgettext('ContractServiceFee', 'Unit of quantity')"
      :label-cols-md="3"
      :content-cols-md="2"
      :labelAlign="labelAlign"
      contentClass="input-group"
      :helpTooltip="
        pgettext(
          'ContractServiceFee|Tooltip',
          'The unit of quantity - if different from the billing quantity.'
        )
      "
    >
      <ItpFormInput
        name="fee_unit_of_quantity"
        v-model="item.unit_of_quantity"
        :value="item.unit_of_quantity"
        @input="
          !($event && typeof $event.stopPropagation === 'function') &&
          (item.unit_of_quantity =
            $event && typeof $event === 'object' && 'data' in $event
              ? $event.data
              : $event)
        "
      >
      </ItpFormInput>
    </ItpFormGroup>
    <ItpFormGroup
      name="fee_inflation_indexed"
      :label="pgettext('ContractServiceFee', 'Inflation indexed')"
      :label-cols-md="3"
      :content-cols-md="9"
      :labelAlign="labelAlign"
      :helpTooltip="
        pgettext(
          'ContractServiceFee|Tooltip',
          'Indicates if the unit price should be automatically adjusted based on indexation rules.'
        )
      "
    >
      <ItpFormCheckbox
        name="fee_inflation_indexed"
        v-model="item.inflation_indexed"
        :value="item.inflation_indexed"
        @input="
          !($event && typeof $event.stopPropagation === 'function') &&
          (item.inflation_indexed =
            $event && typeof $event === 'object' && 'data' in $event
              ? $event.data
              : $event)
        "
      >
      </ItpFormCheckbox>
    </ItpFormGroup>
    <ItpFormGroup
      name="transaction_notes"
      :label="pgettext('ContractServiceFee', 'Invoice notes')"
      :label-cols-md="3"
      :content-cols-md="9"
      :labelAlign="labelAlign"
      :helpTooltip="
        pgettext(
          'ContractServiceFee|Tooltip',
          'Note to display on the invoice line.'
        )
      "
    >
      <TransactionNotesEditor
        v-model="item.transaction_notes"
        :value="item.transaction_notes"
        @input="
          !($event && typeof $event.stopPropagation === 'function') &&
          (item.transaction_notes =
            $event && typeof $event === 'object' && 'data' in $event
              ? $event.data
              : $event)
        "
      >
      </TransactionNotesEditor>
    </ItpFormGroup>
    <ItpFormGroup
      name="fee_notes"
      :label="pgettext('ContractServiceFee', 'Notes')"
      :label-cols-md="3"
      :content-cols-md="9"
      :labelAlign="labelAlign"
      :helpTooltip="
        pgettext('ContractServiceFee|Tooltip', 'Notes for internal use only.')
      "
    >
      <ItpFormTextarea
        name="fee_notes"
        v-model="item.notes"
        :value="item.notes"
        @input="
          !($event && typeof $event.stopPropagation === 'function') &&
          (item.notes =
            $event && typeof $event === 'object' && 'data' in $event
              ? $event.data
              : $event)
        "
      >
      </ItpFormTextarea>
    </ItpFormGroup>
  </ItpUsercontrol>
</template>
